import { useFlag } from '@calendly/feature-check-react'
import {
  createContext,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react'

import { getPushCalendarData, checkIsFreeEmailDomain, handleCheckCalInvalid } from '../utils'


const MeetingIntelligenceContext = createContext()

export const MeetingIntelligenceProvider = ({
  children,
  isLoading,
  meetingIntelligenceData,
  calendarData,
}) => {
  const isNotetakerOutlookSettingsEnabled = useFlag('notetaker_outlook_settings', false)
  const [canDisableMeetingRecaps, setCanDisableMeetingRecaps] = useState(true)
  const [isEnabled, setIsEnabled] = useState(false)
  const [calendlyMeetings, setCalendlyMeetings] = useState(true)
  const [connectedNonCalendlyMeetings, setConnectedNonCalendlyMeetings] = useState(false)
  const [externalInviteeMeetings, setExternalInviteeMeetings] = useState(true)
  const [recordHostedMeetings, setRecordHostedMeetings] = useState(false)
  const [autoShareRecapEmails, setAutoShareRecapEmails] = useState(false)
  const [isValidCalendar, setIsValidCalendar] = useState(false)
  const [email, setEmail] = useState(null)
  const [isFreeDomain, setIsFreeDomain] = useState(false)
  const [kind, setKind] = useState(null)

  const handleSubmit = useCallback(() => ({
    calendlyMeetings,
    connectedNonCalendlyMeetings,
    recordHostedMeetings,
    autoShareRecapEmails,
    externalInviteeMeetings
  }), [calendlyMeetings, connectedNonCalendlyMeetings, externalInviteeMeetings, recordHostedMeetings, autoShareRecapEmails])

  const handleSetValuesToOrigin = useCallback(() => {
    setRecordHostedMeetings(meetingIntelligenceData?.autoRecordV3NonCalendlyHostedMeetingsOnly ?? false)
    setCalendlyMeetings(meetingIntelligenceData?.autoRecordV3Calendly ?? true)
    setConnectedNonCalendlyMeetings(meetingIntelligenceData?.autoRecordV3NonCalendly ?? false)
    setExternalInviteeMeetings(meetingIntelligenceData?.autoRecordV3RequireExternalInvitees ?? true)
    setAutoShareRecapEmails(meetingIntelligenceData?.autoShareRecapEmails ?? false)
  }, [meetingIntelligenceData])

  useEffect(() => {
    if (!isLoading) {
      setIsEnabled(meetingIntelligenceData?.miEnabled ?? false)
      handleSetValuesToOrigin()
      setCanDisableMeetingRecaps(meetingIntelligenceData?.canManageMi ?? false)


      if (calendarData?.length) {
        let calendarAccount = getPushCalendarData(calendarData)

        if (meetingIntelligenceData && Array.isArray(meetingIntelligenceData?.autoRecordV3NonCalendlyAccounts)) {
          const targetUuid = meetingIntelligenceData.autoRecordV3NonCalendlyAccounts[0]
          calendarAccount = calendarData.find((cal) => cal.uuid === targetUuid)
        }
        if (calendarAccount) {
          const pushCalendarId = calendarAccount?.identifier // changed from pushCalendarId to identifier
          // outlook pushCalendarId is a string of letters/numbers that doesn't match the email address
          const calendarKind = calendarAccount?.kind
          const isValid = handleCheckCalInvalid(calendarKind, isNotetakerOutlookSettingsEnabled)
          setEmail(pushCalendarId)
          setIsFreeDomain(checkIsFreeEmailDomain(calendarAccount?.email))
          setIsValidCalendar(isValid)
          setKind(calendarKind)
        }
      }
    }
  }, [calendarData, handleSetValuesToOrigin, isLoading, meetingIntelligenceData, isNotetakerOutlookSettingsEnabled])

  const meetingIntelligenceContextValue = useMemo(
    () => ({
      calendlyMeetings,
      setCalendlyMeetings,
      connectedNonCalendlyMeetings,
      setConnectedNonCalendlyMeetings,
      externalInviteeMeetings,
      setExternalInviteeMeetings,
      recordHostedMeetings,
      setRecordHostedMeetings,
      autoShareRecapEmails,
      setAutoShareRecapEmails,
      isValidCalendar,
      canDisableMeetingRecaps,
      isEnabled,
      email,
      handleSetValuesToOrigin,
      handleSubmit,
      isFreeDomain,
      kind,
    }),
    [
      calendlyMeetings,
      setCalendlyMeetings,
      connectedNonCalendlyMeetings,
      setConnectedNonCalendlyMeetings,
      externalInviteeMeetings,
      setExternalInviteeMeetings,
      recordHostedMeetings,
      setRecordHostedMeetings,
      autoShareRecapEmails,
      setAutoShareRecapEmails,
      isValidCalendar,
      canDisableMeetingRecaps,
      isEnabled,
      email,
      handleSetValuesToOrigin,
      handleSubmit,
      isFreeDomain,
      kind,
    ]
  )

  if (isLoading) return null

  return (
    <MeetingIntelligenceContext.Provider value={meetingIntelligenceContextValue}>
      {children}
    </MeetingIntelligenceContext.Provider>
  )
}

export const useMeetingIntelligenceContext = () => useContext(MeetingIntelligenceContext)
